<template>
  <div class="p-4 md:p-6">
    <!-- Header Premium -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
      <div>
        <div class="flex items-center gap-3 mb-1">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200 dark:shadow-none">
            <Icon icon="mdi:credit-card-settings" class="text-white text-xl" />
          </div>
          <h1 class="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Tipos de Créditos</h1>
        </div>
        <p class="text-sm font-medium text-gray-500 dark:text-gray-400 ml-13">Configure las modalidades de financiamiento del sistema</p>
      </div>
      
      <div class="flex items-center gap-3 w-full md:w-auto">
        <div class="relative group flex-1 md:flex-none">
          <button
            @click="showExportMenu = !showExportMenu"
            class="w-full md:w-auto flex items-center justify-center px-5 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-sm hover:border-blue-500 hover:text-blue-600 transition-all shadow-sm"
          >
            <Icon icon="mdi:file-export" class="mr-2 text-lg" />
            Exportar
            <Icon icon="mdi:chevron-down" class="ml-2 opacity-50" />
          </button>
          
          <transition enter-active-class="transition duration-200 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-150 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
            <div v-if="showExportMenu" 
                 class="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 z-[60] overflow-hidden">
              <div class="p-2">
                <button @click="exportToExcel" class="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl flex items-center transition-all group">
                  <div class="p-2 bg-green-50 dark:bg-green-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Icon icon="mdi:microsoft-excel" class="text-green-600 text-xl" />
                  </div>
                  <div>
                    <p class="font-black text-[11px] uppercase tracking-wider text-gray-400">Excel</p>
                    <p class="text-sm font-bold text-gray-700 dark:text-gray-300">Hoja de Cálculo</p>
                  </div>
                </button>
                <button @click="exportToPDF" class="w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl flex items-center transition-all group mt-1">
                  <div class="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Icon icon="mdi:file-pdf-box" class="text-red-600 text-xl" />
                  </div>
                  <div>
                    <p class="font-black text-[11px] uppercase tracking-wider text-gray-400">PDF</p>
                    <p class="text-sm font-bold text-gray-700 dark:text-gray-300">Documento Listo</p>
                  </div>
                </button>
              </div>
            </div>
          </transition>
        </div>
        
        <button
          @click="abrirModalNuevo"
          class="flex-1 md:flex-none flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-sm hover:shadow-lg hover:shadow-blue-200 dark:hover:shadow-none hover:translate-y-[-1px] transition-all"
        >
          <Icon icon="mdi:plus" class="mr-2 text-xl" />
          NUEVO TIPO
        </button>
      </div>
    </div>

    <!-- Panel de Filtros Premium -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl shadow-gray-100 dark:shadow-none p-6 mb-8 border border-gray-100 dark:border-gray-700 relative overflow-hidden">
      <div class="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
      
      <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
        <!-- Búsqueda -->
        <div class="md:col-span-12 lg:col-span-4">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Búsqueda Inteligente</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-blue-500 transition-colors" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Nombre o descripción..."
              class="w-full pl-12 pr-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-all"
              @input="handleSearch"
            />
          </div>
        </div>

        <!-- Filtros Select -->
        <div class="md:col-span-4 lg:col-span-2">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Estado</label>
          <select
            v-model="filterStatus"
            class="w-full px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-bold appearance-none cursor-pointer"
            @change="handleFilterChange"
          >
            <option value="all">Todos</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
        </div>

        <div class="md:col-span-4 lg:col-span-2">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Aprobación</label>
          <select
            v-model="filterAprobacion"
            class="w-full px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-bold appearance-none cursor-pointer"
            @change="handleFilterChange"
          >
            <option value="all">Cualquiera</option>
            <option value="true">Requiere</option>
            <option value="false">No Requiere</option>
          </select>
        </div>

        <div class="md:col-span-4 lg:col-span-3">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Ordenar por</label>
          <select
            v-model="sortBy"
            class="w-full px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-bold appearance-none cursor-pointer"
          >
            <option value="nombre">Nombre (A-Z)</option>
            <option value="nombre_desc">Nombre (Z-A)</option>
            <option value="interes_mensual">Interés (menor a mayor)</option>
            <option value="interes_mensual_desc">Interés (mayor a menor)</option>
          </select>
        </div>

        <!-- Botón Reset -->
        <div class="md:col-span-12 lg:col-span-1 flex items-end">
          <button
            @click="cargarTiposCredito"
            class="w-full h-[44px] flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-xl hover:bg-blue-500 hover:text-white transition-all group"
            title="Reiniciar filtros"
          >
            <Icon icon="mdi:refresh" class="text-xl group-hover:rotate-180 transition-transform duration-500" :class="{ 'animate-spin': loading }" />
          </button>
        </div>
      </div>
    </div>
    <!-- Estadísticas Premium -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 mt-2">
      <!-- Total Tipos -->
      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
            <Icon icon="mdi:credit-card-multiple" class="text-blue-600 dark:text-blue-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Modalidades</p>
            <h3 class="text-2xl font-black text-gray-900 dark:text-white">{{ tiposCreditos.length }}</h3>
          </div>
        </div>
      </div>

      <!-- Activos -->
      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
            <Icon icon="mdi:check-decagram" class="text-emerald-600 dark:text-emerald-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Activos</p>
            <h3 class="text-2xl font-black text-emerald-600 dark:text-emerald-400">{{ estadisticas.activos }}</h3>
          </div>
        </div>
      </div>

      <!-- Inactivos -->
      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
            <Icon icon="mdi:close-circle" class="text-red-600 dark:text-red-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Inactivos</p>
            <h3 class="text-2xl font-black text-red-600 dark:text-red-400">{{ estadisticas.inactivos }}</h3>
          </div>
        </div>
      </div>

      <!-- Interés Promedio -->
      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
            <Icon icon="mdi:percent" class="text-amber-600 dark:text-amber-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tasa Media</p>
            <h3 class="text-2xl font-black text-amber-600 dark:text-amber-400">{{ estadisticas.interesPromedio }}%</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
        <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
          <Icon icon="mdi:filter" class="inline mr-1" />
          Filtros aplicados:
        </span>
        <span v-if="searchQuery" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ searchQuery }}"
          <button @click="searchQuery = ''" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterStatus !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Estado: {{ filterStatus === 'activo' ? 'Activos' : 'Inactivos' }}
          <button @click="filterStatus = 'all'; handleFilterChange()" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterAprobacion !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
          {{ filterAprobacion === 'true' ? 'Requiere aprobación' : 'No requiere aprobación' }}
          <button @click="filterAprobacion = 'all'; handleFilterChange()" class="ml-1.5 hover:text-purple-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <button
          @click="clearFilters"
          class="text-sm text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-medium"
        >
          <Icon icon="mdi:filter-off" class="inline mr-1" />
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Tabla de tipos de créditos -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <!-- Encabezado de tabla -->
      <div class="px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h2 class="text-base md:text-lg font-semibold text-gray-800 dark:text-white">Lista de Tipos de Créditos</h2>
          <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ filteredTipos.length }} de {{ tiposCreditos.length }} tipos
          </p>
        </div>
        <div class="flex items-center space-x-2 text-xs md:text-sm text-gray-500 dark:text-gray-400">
          <Icon icon="mdi:calendar" class="inline mr-1 text-sm" />
          <span class="hidden xs:inline">Última actualización:</span> {{ formatTime(lastUpdate) }}
        </div>
      </div>

      <!-- Tabla -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">ID</th>
              <th class="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Nombre</span>
                  <button @click="sortBy = sortBy === 'nombre' ? 'nombre_desc' : 'nombre'">
                    <Icon icon="mdi:sort" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Descripción</th>
              <th class="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Requiere Aprob.</th>
              <th class="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Interés %</span>
                  <button @click="sortBy = sortBy === 'interes_mensual' ? 'interes_mensual_desc' : 'interes_mensual'">
                    <Icon icon="mdi:sort" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
              <th class="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <!-- Estado de carga -->
            <tr v-if="loading">
              <td colspan="7" class="px-4 md:px-6 py-12">
                <div class="flex flex-col items-center justify-center">
                  <div class="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                  <p class="text-sm md:text-base text-gray-600 dark:text-gray-400">Cargando tipos de créditos...</p>
                </div>
              </td>
            </tr>

            <!-- Sin resultados -->
            <tr v-else-if="filteredTipos.length === 0">
              <td colspan="7" class="px-4 md:px-6 py-12 md:py-16 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:credit-card-remove" class="text-3xl md:text-4xl text-gray-400" />
                  </div>
                  <h3 class="text-base md:text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No se encontraron tipos de crédito
                  </h3>
                  <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                    {{ searchQuery || filterStatus !== 'all' || filterAprobacion !== 'all'
                      ? 'Intenta con otros términos de búsqueda o cambia los filtros.' 
                      : 'No hay tipos de crédito registrados. ¡Crea el primero!' }}
                  </p>
                  <button
                    v-if="!searchQuery && filterStatus === 'all' && filterAprobacion === 'all'"
                    @click="abrirModalNuevo"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Icon icon="mdi:plus" class="mr-2 inline" />
                    Crear primer tipo
                  </button>
                  <button
                    v-else
                    @click="clearFilters"
                    class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm"
                  >
                    <Icon icon="mdi:filter-off" class="mr-2 inline" />
                    Limpiar filtros
                  </button>
                </div>
              </td>
            </tr>

            <!-- Lista de tipos de créditos -->
            <tr 
              v-else 
              v-for="tipo in paginatedTipos" 
              :key="tipo.idTipoCredito"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 cursor-pointer"
              @click="abrirModalDetalle(tipo)"
            >
              <td class="px-4 md:px-6 py-4 whitespace-nowrap">
                <div class="text-xs md:text-sm font-medium text-gray-900 dark:text-white">
                  {{ tipo.idTipoCredito }}
                </div>
              </td>
              <td class="px-4 md:px-6 py-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10 md:h-12 md:w-12">
                    <div :class="[
                      'h-10 w-10 md:h-12 md:w-12 rounded-xl flex items-center justify-center shadow-sm',
                      tipo.estado === 'activo'
                        ? 'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900'
                    ]">
                      <Icon 
                        icon="mdi:credit-card" 
                        :class="[
                          'text-lg md:text-xl',
                          tipo.estado === 'activo'
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-500 dark:text-gray-400'
                        ]" 
                      />
                    </div>
                  </div>
                  <div class="ml-3 md:ml-4">
                    <div class="text-xs md:text-sm font-semibold text-gray-900 dark:text-white">
                      {{ tipo.nombre }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-4 md:px-6 py-4">
                <div class="text-xs md:text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                  {{ tipo.descripcion || '-' }}
                </div>
              </td>
              <td class="px-4 md:px-6 py-4 whitespace-nowrap">
                <span
                  :class="[
                    'px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full',
                    tipo.requiere_aprobacion
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  ]"
                >
                  {{ tipo.requiere_aprobacion ? 'Sí' : 'No' }}
                </span>
              </td>
              <td class="px-4 md:px-6 py-4 whitespace-nowrap">
                <div class="text-xs md:text-sm font-medium text-blue-600 dark:text-blue-400">
                  {{ tipo.interes_mensual }}%
                </div>
              </td>
              <td class="px-4 md:px-6 py-4 whitespace-nowrap">
                <span
                  @click.stop="confirmarToggleStatus(tipo)"
                  :class="[
                    'px-2 py-1 md:px-3 md:py-1.5 inline-flex text-xs leading-4 font-semibold rounded-full shadow-sm transition-all duration-300 cursor-pointer',
                    tipo.estado === 'activo'
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900 text-green-800 dark:text-green-200 hover:from-green-200 hover:to-emerald-200 dark:hover:from-green-800 dark:hover:to-emerald-800'
                      : 'bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900 dark:to-pink-900 text-red-800 dark:text-red-200 hover:from-red-200 hover:to-pink-200 dark:hover:from-red-800 dark:hover:to-pink-800'
                  ]"
                >
                  <Icon :icon="tipo.estado === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" class="mr-1.5 text-xs" />
                  {{ tipo.estado === 'activo' ? 'Activo' : 'Inactivo' }}
                </span>
              </td>
              <td class="px-4 md:px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex items-center space-x-1">
                  <button
                    @click.stop="abrirModalEditar(tipo)"
                    class="p-1.5 md:p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" class="text-base md:text-lg" />
                  </button>
                  <button
                    @click.stop="confirmarToggleStatus(tipo)"
                    :class="[
                      'p-1.5 md:p-2 rounded-lg transition-colors',
                      tipo.estado === 'activo'
                        ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800/50'
                        : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800/50'
                    ]"
                    :title="tipo.estado === 'activo' ? 'Desactivar' : 'Activar'"
                  >
                    <Icon :icon="tipo.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-base md:text-lg" />
                  </button>
                  <button
                    @click.stop="confirmarEliminar(tipo)"
                    class="p-1.5 md:p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" class="text-base md:text-lg" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación Premium -->
      <div v-if="filteredTipos.length > 0 && !loading" class="px-6 py-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-2xl">
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div class="text-sm font-medium text-gray-500 dark:text-gray-400">
            Mostrando <span class="text-gray-900 dark:text-white">{{ startItem }}-{{ endItem }}</span> de 
            <span class="text-gray-900 dark:text-white">{{ filteredTipos.length }}</span> resultados
          </div>
          
          <div class="flex items-center space-x-1">
            <!-- Anterior -->
            <button
              @click="currentPage = Math.max(currentPage - 1, 1)"
              :disabled="currentPage === 1"
              class="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <Icon icon="mdi:chevron-left" class="text-xl" />
            </button>
            
            <!-- Páginas -->
            <div class="flex items-center space-x-1">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="currentPage = page"
                :class="[
                  'w-10 h-10 flex items-center justify-center rounded-xl font-bold text-sm transition-all shadow-sm',
                  currentPage === page
                    ? 'bg-blue-600 text-white shadow-blue-200 dark:shadow-none translate-y-[-2px]'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-gray-700'
                ]"
              >
                {{ page }}
              </button>
            </div>
            
            <!-- Siguiente -->
            <button
              @click="currentPage = Math.min(currentPage + 1, totalPages)"
              :disabled="currentPage === totalPages"
              class="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <Icon icon="mdi:chevron-right" class="text-xl" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Crear/Editar -->
    <Teleport to="body">
      <div v-if="mostrarModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden transform transition-all">
          <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-indigo-600">
            <div class="flex justify-between items-center text-white">
              <div class="flex items-center gap-3">
                <div class="p-2 bg-white/20 rounded-xl">
                  <Icon :icon="modoEdicion ? 'mdi:pencil-box' : 'mdi:credit-card-plus'" class="text-2xl" />
                </div>
                <div>
                  <h3 class="text-xl font-black tracking-tight">{{ modoEdicion ? 'Editar' : 'Nuevo' }} Tipo de Crédito</h3>
                  <p class="text-xs text-blue-100 font-medium">Gestión de modalidades financieras</p>
                </div>
              </div>
              <button @click="cerrarModal" class="p-2 hover:bg-white/20 rounded-xl transition-colors">
                <Icon icon="mdi:close" class="text-2xl" />
              </button>
            </div>
          </div>
          
          <form @submit.prevent="guardarTipoCredito" class="p-6 space-y-5">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div class="md:col-span-2">
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Nombre de la Modalidad *</label>
                <input
                  v-model="formData.nombre"
                  type="text"
                  required
                  placeholder="Ej: CAShea, Crédito Directo..."
                  class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-bold"
                />
              </div>
              
              <div class="md:col-span-2">
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Descripción</label>
                <textarea
                  v-model="formData.descripcion"
                  rows="2"
                  placeholder="Detalles sobre este tipo de crédito..."
                  class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                ></textarea>
              </div>
              
              <div>
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Interés Mensual (%) *</label>
                <div class="relative group">
                  <span class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>
                  <input
                    v-model.number="formData.interes_mensual"
                    type="number"
                    step="0.01"
                    required
                    class="w-full pl-9 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-bold"
                  />
                </div>
              </div>
              
              <div class="flex items-center pt-6">
                <label class="relative inline-flex items-center cursor-pointer group">
                  <input type="checkbox" v-model="formData.requiere_aprobacion" class="sr-only peer">
                  <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600 transition-all"></div>
                  <span class="ms-3 text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-tighter">¿Requiere Aprobación?</span>
                </label>
              </div>

              <div class="md:col-span-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3 ml-1">Estado del Sistema</label>
                <div class="flex gap-4">
                  <button 
                    type="button"
                    @click="formData.estado = 'activo'"
                    :class="[
                      'flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all border-2',
                      formData.estado === 'activo' 
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-900/20' 
                        : 'bg-white border-gray-100 text-gray-400 dark:bg-gray-800 dark:border-gray-700'
                    ]"
                  >
                    Activo
                  </button>
                  <button 
                    type="button"
                    @click="formData.estado = 'inactivo'"
                    :class="[
                      'flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all border-2',
                      formData.estado === 'inactivo' 
                        ? 'bg-red-50 border-red-500 text-red-700 dark:bg-red-900/20' 
                        : 'bg-white border-gray-100 text-gray-400 dark:bg-gray-800 dark:border-gray-700'
                    ]"
                  >
                    Inactivo
                  </button>
                </div>
              </div>
            </div>

            <div class="flex gap-3 pt-4">
              <button
                type="button"
                @click="cerrarModal"
                class="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 font-bold text-sm hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="saving || !isFormValid"
                class="flex-[2] px-4 py-3 bg-blue-600 text-white rounded-xl font-black text-sm hover:bg-blue-700 shadow-lg shadow-blue-200 dark:shadow-none transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Icon v-if="saving" icon="mdi:loading" class="animate-spin text-xl" />
                <Icon v-else :icon="modoEdicion ? 'mdi:check-bold' : 'mdi:plus-thick'" class="text-xl" />
                {{ saving ? 'GUARDANDO...' : (modoEdicion ? 'ACTUALIZAR' : 'CREAR MODALIDAD') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Modal de detalles -->
    <div v-if="showDetailModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100 mx-auto my-auto overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="p-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 shadow-md mr-3">
                <Icon icon="mdi:credit-card-details" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-lg font-bold text-gray-900 dark:text-white">
                  Detalles del Tipo de Crédito
                </h3>
                <p class="text-xs text-gray-600 dark:text-gray-400">Información completa</p>
              </div>
            </div>
            <button @click="closeDetailModal" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>
        </div>
        
        <div v-if="selectedTipo" class="px-6 py-4 max-h-[70vh] overflow-y-auto">
          <div class="space-y-4">
            <div class="flex items-center space-x-4 mb-4">
              <div class="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500">
                <Icon icon="mdi:credit-card" class="text-white text-2xl" />
              </div>
              <div>
                <h4 class="text-base font-semibold text-gray-900 dark:text-white">
                  {{ selectedTipo.nombre }}
                </h4>
                <p class="text-xs text-gray-500 dark:text-gray-400">ID: {{ selectedTipo.idTipoCredito }}</p>
              </div>
            </div>
            
            <div class="space-y-3">
              <!-- Descripción -->
              <div v-if="selectedTipo.descripcion" class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div class="flex items-start">
                  <Icon icon="mdi:card-text" class="text-gray-500 mr-3 text-lg" />
                  <div>
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Descripción</p>
                    <p class="text-sm text-gray-900 dark:text-white">{{ selectedTipo.descripcion }}</p>
                  </div>
                </div>
              </div>
              
              <!-- Requiere Aprobación -->
              <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div class="flex items-start">
                  <Icon icon="mdi:check-circle-outline" class="text-gray-500 mr-3 text-lg" />
                  <div>
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Requiere Aprobación</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      <span
                        :class="[
                          'px-2 py-1 inline-flex text-xs font-semibold rounded-full',
                          selectedTipo.requiere_aprobacion
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        ]"
                      >
                        {{ selectedTipo.requiere_aprobacion ? 'Sí' : 'No' }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              
              <!-- Interés -->
              <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div class="flex items-start">
                  <Icon icon="mdi:percent" class="text-gray-500 mr-3 text-lg" />
                  <div>
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Interés Mensual</p>
                    <p class="text-sm font-bold text-blue-600 dark:text-blue-400">{{ selectedTipo.interes_mensual }}%</p>
                  </div>
                </div>
              </div>
              
              <!-- Estado -->
              <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Estado</p>
                <span
                  :class="[
                    'px-3 py-1.5 inline-flex text-xs font-semibold rounded-full',
                    selectedTipo.estado === 'activo'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  ]"
                >
                  {{ selectedTipo.estado === 'activo' ? 'Activo' : 'Inactivo' }}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            @click="abrirModalEditar(selectedTipo); closeDetailModal()"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center"
          >
            <Icon icon="mdi:pencil" class="mr-2" />
            Editar
          </button>
          <button
            @click="closeDetailModal"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de confirmación -->
    <div v-if="showConfirmModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[150]" @click.self="cancelarConfirmacion">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100 mx-auto my-auto overflow-hidden">
        <div class="px-6 py-4" :class="confirmModalColor">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-white bg-opacity-30 mr-4">
              <Icon :icon="confirmModalIcon" class="text-white text-2xl" />
            </div>
            <div>
              <h3 class="text-lg font-bold text-white">{{ confirmModalTitulo }}</h3>
              <p class="text-sm text-white text-opacity-90">{{ confirmModalSubtitulo }}</p>
            </div>
          </div>
        </div>
        
        <div class="px-6 py-5">
          <p class="text-gray-700 dark:text-gray-300 mb-2">{{ confirmModalMensaje }}</p>
          <p v-if="confirmModalDetalle" class="text-sm text-gray-500 dark:text-gray-400 mb-4">{{ confirmModalDetalle }}</p>
          
          <div v-if="confirmModalTipo === 'eliminar' && confirmModalTipoCredito" class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <div class="flex items-center">
              <Icon icon="mdi:alert-circle" class="text-red-600 dark:text-red-400 text-xl mr-2" />
              <div>
                <p class="font-medium text-gray-900 dark:text-white">{{ confirmModalTipoCredito.nombre }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">ID: {{ confirmModalTipoCredito.idTipoCredito }}</p>
              </div>
            </div>
            <p class="mt-2 text-xs text-red-600 dark:text-red-400">
              <Icon icon="mdi:alert" class="inline mr-1" />
              Esta acción no se puede deshacer
            </p>
          </div>

          <div v-if="confirmModalTipo === 'toggle-status' && confirmModalTipoCredito" class="mb-4 p-3 rounded-lg" :class="confirmModalTipoCredito.estado === 'activo' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'">
            <div class="flex items-center">
              <Icon :icon="confirmModalTipoCredito.estado === 'activo' ? 'mdi:close-circle' : 'mdi:check-circle'" class="text-xl mr-2" :class="confirmModalTipoCredito.estado === 'activo' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'" />
              <div>
                <p class="font-medium text-gray-900 dark:text-white">{{ confirmModalTipoCredito.nombre }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">Estado actual: {{ confirmModalTipoCredito.estado === 'activo' ? 'Activo' : 'Inactivo' }}</p>
              </div>
            </div>
            <p class="mt-2 text-xs">
              El tipo de crédito pasará a estado: 
              <span class="font-semibold" :class="confirmModalTipoCredito.estado === 'activo' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'">
                {{ confirmModalTipoCredito.estado === 'activo' ? 'Inactivo' : 'Activo' }}
              </span>
            </p>
          </div>
        </div>
        
        <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            @click="cancelarConfirmacion"
            class="px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 text-sm"
          >
            Cancelar
          </button>
          <button
            @click="ejecutarAccionConfirmada"
            class="px-4 py-2 rounded-lg text-white font-medium transition-all duration-300 text-sm flex items-center"
            :class="confirmModalButtonColor"
          >
            <Icon v-if="confirmModalCargando" icon="mdi:loading" class="animate-spin mr-2" />
            {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de alerta -->
    <div v-if="showAlertModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[200]" @click.self="closeAlert">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100 mx-auto my-auto overflow-hidden">
        <div class="px-6 py-4" :class="alertColor">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-white bg-opacity-30 mr-4">
              <Icon :icon="alertIcon" class="text-white text-2xl" />
            </div>
            <div>
              <h3 class="text-lg font-bold text-white">{{ alertTitulo }}</h3>
              <p class="text-sm text-white text-opacity-90">{{ alertSubtitulo }}</p>
            </div>
          </div>
        </div>
        
        <div class="px-6 py-5">
          <p class="text-gray-700 dark:text-gray-300">{{ alertMensaje }}</p>
          <p v-if="alertDetalle" class="mt-2 text-sm text-gray-500 dark:text-gray-400">{{ alertDetalle }}</p>
        </div>
        
        <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            @click="closeAlert"
            class="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 text-sm"
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import {
  obtenerTiposCredito,
  crearTipoCredito,
  actualizarTipoCredito,
  eliminarTipoCredito,
  cambiarEstadoTipoCredito,
  activarTipoCredito,
  desactivarTipoCredito,
  obtenerEstadisticas,
  ordenarTiposCredito,
  buscarTiposCredito,
  filtrarPorEstado,
  filtrarPorAprobacion,
  validarNombre,
  validarInteresMensual,
  formatearNombre,
  convertirEstadoATexto,
  exportarExcel,
  exportarPDF,
  DEFAULT_INTERES_MENSUAL
} from '@/api/tipoCredito.js';

import logoJean from '@/assets/logo-jean.jpg';

export default {
  name: 'TiposCreditosList',
  components: {
    Icon
  },
  data() {
    return {
      tiposCreditos: [],
      loading: false,
      saving: false,
      mostrarModal: false,
      showDetailModal: false,
      showExportMenu: false,
      modoEdicion: false,
      searchQuery: '',
      filterStatus: 'all',
      filterAprobacion: 'all',
      sortBy: 'nombre',
      currentPage: 1,
      itemsPerPage: 10,
      formData: {
        idTipoCredito: null,
        nombre: '',
        descripcion: '',
        requiere_aprobacion: false,
        interes_mensual: 0,
        estado: 'activo'
      },
      errors: {},
      selectedTipo: null,
      lastUpdate: new Date(),

      // Variables para modal de confirmación
      showConfirmModal: false,
      confirmModalTipo: '',
      confirmModalTitulo: '',
      confirmModalSubtitulo: '',
      confirmModalMensaje: '',
      confirmModalDetalle: '',
      confirmModalIcon: '',
      confirmModalColor: '',
      confirmModalButtonColor: '',
      confirmModalBotonTexto: '',
      confirmModalTipoCredito: null,
      confirmModalCargando: false,
      confirmModalAccion: null,
      confirmModalDatos: null,

      // Variables para modal de alerta
      showAlertModal: false,
      alertTipo: '',
      alertTitulo: '',
      alertSubtitulo: '',
      alertMensaje: '',
      alertDetalle: '',
      alertIcon: '',
      alertColor: '',
    };
  },
  computed: {
    filteredTipos() {
      let filtered = [...this.tiposCreditos];
      
      // Aplicar búsqueda
      if (this.searchQuery.trim()) {
        filtered = buscarTiposCredito(filtered, this.searchQuery);
      }
      
      // Aplicar filtro de estado
      filtered = filtrarPorEstado(filtered, this.filterStatus);
      
      // Aplicar filtro de aprobación
      if (this.filterAprobacion !== 'all') {
        filtered = filtrarPorAprobacion(filtered, this.filterAprobacion === 'true');
      }
      
      // Aplicar ordenamiento
      filtered = ordenarTiposCredito(filtered, this.sortBy);
      
      return filtered;
    },
    
    estadisticas() {
      return obtenerEstadisticas(this.tiposCreditos);
    },
    
    paginatedTipos() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredTipos.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredTipos.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredTipos.length);
    },
    
    isFormValid() {
      return this.formData.nombre.trim() && 
             validarInteresMensual(this.formData.interes_mensual) &&
             this.validateForm();
    },
    
    isFiltered() {
      return this.searchQuery.trim() || this.filterStatus !== 'all' || this.filterAprobacion !== 'all';
    },
  },
  mounted() {
    this.cargarTiposCredito();
  },
  watch: {
    filterStatus() {
      this.currentPage = 1;
    },
    filterAprobacion() {
      this.currentPage = 1;
    },
    searchQuery: debounce(function() {
      this.currentPage = 1;
    }, 300),
    sortBy() {
      this.currentPage = 1;
    },
  },
  methods: {
    // ========== MÉTODOS DE ALERTA ==========
    mostrarAlerta(config) {
      this.alertTipo = config.tipo;
      this.alertTitulo = config.titulo;
      this.alertSubtitulo = config.subtitulo || '';
      this.alertMensaje = config.mensaje;
      this.alertDetalle = config.detalle || '';
      
      switch (config.tipo) {
        case 'success':
          this.alertIcon = 'mdi:check-circle';
          this.alertColor = 'bg-gradient-to-r from-green-600 to-emerald-600';
          break;
        case 'error':
          this.alertIcon = 'mdi:alert-circle';
          this.alertColor = 'bg-gradient-to-r from-red-600 to-pink-600';
          break;
        case 'warning':
          this.alertIcon = 'mdi:alert';
          this.alertColor = 'bg-gradient-to-r from-orange-500 to-amber-500';
          break;
        case 'info':
        default:
          this.alertIcon = 'mdi:information';
          this.alertColor = 'bg-gradient-to-r from-blue-600 to-indigo-600';
          break;
      }
      
      this.showAlertModal = true;
      
      if (config.tipo === 'success') {
        setTimeout(() => {
          this.closeAlert();
        }, 3000);
      }
    },

    closeAlert() {
      this.showAlertModal = false;
    },

    // ========== MÉTODOS DE CONFIRMACIÓN ==========
    abrirModalConfirmacion(config) {
      this.confirmModalTipo = config.tipo;
      this.confirmModalTitulo = config.titulo;
      this.confirmModalSubtitulo = config.subtitulo || '';
      this.confirmModalMensaje = config.mensaje;
      this.confirmModalDetalle = config.detalle || '';
      this.confirmModalIcon = config.icon || 'mdi:help-circle';
      this.confirmModalColor = config.color || 'bg-gradient-to-r from-blue-600 to-indigo-600';
      this.confirmModalButtonColor = config.buttonColor || 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700';
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar';
      this.confirmModalTipoCredito = config.tipoCredito || null;
      this.confirmModalAccion = config.accion;
      this.confirmModalDatos = config.datos || null;
      this.showConfirmModal = true;
    },

    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalTipoCredito = null;
      this.confirmModalAccion = null;
      this.confirmModalDatos = null;
    },

    async ejecutarAccionConfirmada() {
      if (this.confirmModalAccion) {
        this.confirmModalCargando = true;
        await this.confirmModalAccion(this.confirmModalDatos);
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },

    confirmarEliminar(tipo) {
      this.abrirModalConfirmacion({
        tipo: 'eliminar',
        titulo: 'Eliminar Tipo de Crédito',
        subtitulo: 'Esta acción no se puede deshacer',
        mensaje: `¿Estás seguro de eliminar "${tipo.nombre}"?`,
        detalle: 'El tipo de crédito será eliminado permanentemente del sistema.',
        icon: 'mdi:alert-circle',
        color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700',
        botonTexto: 'Eliminar',
        tipoCredito: tipo,
        accion: () => this.eliminarTipoCredito(tipo.idTipoCredito)
      });
    },

    confirmarToggleStatus(tipo) {
      this.abrirModalConfirmacion({
        tipo: 'toggle-status',
        titulo: tipo.estado === 'activo' ? 'Desactivar Tipo de Crédito' : 'Activar Tipo de Crédito',
        subtitulo: 'Cambiar estado',
        mensaje: `¿Estás seguro de ${tipo.estado === 'activo' ? 'desactivar' : 'activar'} "${tipo.nombre}"?`,
        detalle: `El tipo de crédito pasará de "${tipo.estado === 'activo' ? 'Activo' : 'Inactivo'}" a "${tipo.estado === 'activo' ? 'Inactivo' : 'Activo'}".`,
        icon: tipo.estado === 'activo' ? 'mdi:close-circle' : 'mdi:check-circle',
        color: tipo.estado === 'activo' ? 'bg-gradient-to-r from-red-600 to-orange-600' : 'bg-gradient-to-r from-green-600 to-emerald-600',
        buttonColor: tipo.estado === 'activo' ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700' : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
        botonTexto: tipo.estado === 'activo' ? 'Desactivar' : 'Activar',
        tipoCredito: tipo,
        accion: () => this.toggleStatus(tipo)
      });
    },

    // ========== MÉTODOS PRINCIPALES ==========
    async cargarTiposCredito() {
      this.loading = true;
      try {
        const response = await obtenerTiposCredito();
        
        if (response.success) {
          this.tiposCreditos = response.data || [];
          this.lastUpdate = new Date();
          console.log('✅ Tipos de crédito cargados:', this.tiposCreditos.length);
        } else {
          throw new Error(response.error || 'Error al cargar tipos de crédito');
        }
      } catch (error) {
        console.error('❌ Error cargando tipos de crédito:', error);
        this.mostrarAlerta({
          tipo: 'error',
          titulo: 'Error',
          subtitulo: 'No se pudieron cargar los tipos de crédito',
          mensaje: error.message || 'Error al cargar los datos',
          detalle: 'Por favor, intenta de nuevo más tarde'
        });
      } finally {
        this.loading = false;
      }
    },

    handleFilterChange() {
      this.currentPage = 1;
    },

    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),

    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = 'all';
      this.filterAprobacion = 'all';
      this.sortBy = 'nombre';
      this.currentPage = 1;
    },

    abrirModalNuevo() {
      this.modoEdicion = false;
      this.formData = {
        idTipoCredito: null,
        nombre: '',
        descripcion: '',
        requiere_aprobacion: false,
        interes_mensual: 0,
        estado: 'activo'
      };
      this.errors = {};
      this.mostrarModal = true;
    },

    abrirModalEditar(tipo) {
      this.modoEdicion = true;
      this.formData = { ...tipo };
      this.errors = {};
      this.mostrarModal = true;
    },

    abrirModalDetalle(tipo) {
      this.selectedTipo = tipo;
      this.showDetailModal = true;
    },

    cerrarModal() {
      this.mostrarModal = false;
      this.errors = {};
    },

    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedTipo = null;
    },

    validateForm() {
      this.errors = {};

      if (!this.formData.nombre.trim()) {
        this.errors.nombre = 'El nombre es requerido';
      } else if (!validarNombre(this.formData.nombre)) {
        this.errors.nombre = 'El nombre contiene caracteres no válidos';
      }

      if (!validarInteresMensual(this.formData.interes_mensual)) {
        this.errors.interes_mensual = 'El interés debe ser un número entre 0 y 100';
      }

      return Object.keys(this.errors).length === 0;
    },

    async guardarTipoCredito() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        let result;
        
        if (this.modoEdicion) {
          result = await actualizarTipoCredito(this.formData.idTipoCredito, this.formData);
        } else {
          result = await crearTipoCredito(this.formData);
        }

        if (result.success) {
          await this.cargarTiposCredito();
          this.cerrarModal();
          this.mostrarAlerta({
            tipo: 'success',
            titulo: this.modoEdicion ? '¡Actualizado!' : '¡Creado!',
            subtitulo: 'Operación exitosa',
            mensaje: this.modoEdicion 
              ? 'Tipo de crédito actualizado correctamente' 
              : 'Tipo de crédito creado correctamente',
            detalle: `"${this.formData.nombre}"`
          });
        } else {
          throw new Error(result.error || 'Error al guardar');
        }
      } catch (error) {
        console.error('❌ Error guardando:', error);
        this.mostrarAlerta({
          tipo: 'error',
          titulo: 'Error',
          subtitulo: 'No se pudo guardar',
          mensaje: error.message || 'Error al guardar el tipo de crédito',
          detalle: 'Por favor, intenta de nuevo'
        });
      } finally {
        this.saving = false;
      }
    },

    async eliminarTipoCredito(id) {
      try {
        const result = await eliminarTipoCredito(id);
        
        if (result.success) {
          await this.cargarTiposCredito();
          this.mostrarAlerta({
            tipo: 'success',
            titulo: '¡Eliminado!',
            subtitulo: 'Operación exitosa',
            mensaje: 'Tipo de crédito eliminado correctamente',
            detalle: 'El registro ha sido eliminado del sistema'
          });
        } else {
          throw new Error(result.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error eliminando:', error);
        this.mostrarAlerta({
          tipo: 'error',
          titulo: 'Error',
          subtitulo: 'No se pudo eliminar',
          mensaje: error.message || 'Error al eliminar el tipo de crédito',
          detalle: 'Es posible que tenga créditos asociados'
        });
      }
    },

    async toggleStatus(tipo) {
      try {
        let result;
        
        if (tipo.estado === 'activo') {
          result = await desactivarTipoCredito(tipo.idTipoCredito);
        } else {
          result = await activarTipoCredito(tipo.idTipoCredito);
        }
        
        if (result.success) {
          await this.cargarTiposCredito();
          this.mostrarAlerta({
            tipo: 'success',
            titulo: '¡Estado cambiado!',
            subtitulo: 'Operación exitosa',
            mensaje: `El tipo de crédito ha sido ${tipo.estado === 'activo' ? 'desactivado' : 'activado'} correctamente`,
            detalle: `Nuevo estado: ${tipo.estado === 'activo' ? 'Inactivo' : 'Activo'}`
          });
        } else {
          throw new Error(result.error || 'Error al cambiar estado');
        }
      } catch (error) {
        console.error('❌ Error cambiando estado:', error);
        this.mostrarAlerta({
          tipo: 'error',
          titulo: 'Error',
          subtitulo: 'No se pudo cambiar el estado',
          mensaje: error.message || 'Error al cambiar el estado',
          detalle: 'Por favor, intenta de nuevo'
        });
      }
    },

    // ========== MÉTODOS DE EXPORTACIÓN ==========
    async exportToExcel() {
      if (this.filteredTipos.length === 0) {
        this.mostrarAlerta({
          tipo: 'warning',
          titulo: 'Sin datos',
          subtitulo: 'No hay información para exportar',
          mensaje: 'No hay tipos de crédito que coincidan con los filtros actuales',
          detalle: 'Prueba con otros filtros o crea nuevos tipos'
        });
        return;
      }

      try {
        const result = exportarExcel(this.filteredTipos, 'tipos_credito_jean_informatico');
        
        if (result.success) {
          this.mostrarAlerta({
            tipo: 'success',
            titulo: '¡Exportado!',
            subtitulo: 'Archivo generado',
            mensaje: `Se ha generado el archivo ${result.filename}`,
            detalle: `${result.count} tipos de crédito exportados correctamente`
          });
          this.showExportMenu = false;
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('❌ Error exportando a Excel:', error);
        this.mostrarAlerta({
          tipo: 'error',
          titulo: 'Error al exportar',
          subtitulo: 'No se pudo generar el archivo',
          mensaje: error.message || 'Error al exportar a Excel',
          detalle: 'Verifica que la librería xlsx esté instalada'
        });
      }
    },

    async exportToPDF() {
      if (this.filteredTipos.length === 0) {
        this.mostrarAlerta({
          tipo: 'warning',
          titulo: 'Sin datos',
          subtitulo: 'No hay información para exportar',
          mensaje: 'No hay tipos de crédito que coincidan con los filtros actuales',
          detalle: 'Prueba con otros filtros o crea nuevos tipos'
        });
        return;
      }

      try {
        const result = exportarPDF(
          this.filteredTipos, 
          'Reporte de Tipos de Crédito - Jean Informatico C.A.',
          logoJean
        );
        
        if (result.success) {
          this.mostrarAlerta({
            tipo: 'success',
            titulo: '¡Exportado!',
            subtitulo: 'Documento generado',
            mensaje: `Se ha generado el archivo ${result.filename}`,
            detalle: result.withLogo ? 'PDF generado con logo de la empresa' : `${result.count} tipos de crédito exportados`
          });
          this.showExportMenu = false;
        } else {
          throw new Error(result.error || 'Error al generar PDF');
        }
      } catch (error) {
        console.error('❌ Error exportando a PDF:', error);
        this.mostrarAlerta({
          tipo: 'error',
          titulo: 'Error al exportar',
          subtitulo: 'No se pudo generar el PDF',
          mensaje: error.message || 'Error al exportar a PDF',
          detalle: 'Verifica que la librería jspdf esté instalada'
        });
      }
    },

    // ========== MÉTODOS AUXILIARES ==========
    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleTimeString('es-VE', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      } catch (error) {
        return '';
      }
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark ::-webkit-scrollbar-track {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

@media (max-width: 768px) {
  button {
    min-height: 44px;
  }
  
  input, select, textarea {
    font-size: 16px !important;
  }
}
</style>